import { useSearchAffiliatesQuery } from '@/hooks/queries/use-search-affiliates-query';
import type { RecipientData } from '@/stores/affiliate-bulk-email-store';
import type { CompanionEmailCtaRequest } from '@/stores/companion-panel-store';
import { useMemo } from 'react';

/** The page the recipient picker itself asks for, so both share one fetch. */
const RECIPIENT_LOOKUP_PER_PAGE = 50;

/**
 * The one recipient a card's email call to action puts in front of the composer.
 *
 * A card carries ids and a display name, never an address: the address belongs
 * to the affiliate and changes without the card knowing, so it is read from the
 * composer's own recipient list instead of being frozen into the card. That
 * list is the query the picker already runs, which means the lookup usually
 * costs nothing and can never disagree with what the picker shows.
 *
 * Until it answers, the name the card carries stands on its own: the brand
 * reads who they are writing to from the very first frame.
 */
export const useCompanionEmailRecipient = (
  request: CompanionEmailCtaRequest | null
): RecipientData | null => {
  const { data } = useSearchAffiliatesQuery(
    { perPage: RECIPIENT_LOOKUP_PER_PAGE, search: request?.recipientName ?? undefined },
    Boolean(request)
  );

  return useMemo(() => {
    if (!request) return null;

    const known = data?.data.find((affiliate) => affiliate.id === request.affiliateUserId);

    return {
      id: request.affiliateUserId,
      name: known?.name ?? request.recipientName ?? '',
      email: known?.email ?? '',
      avatarUrl: known?.avatar?.fileUrl,
      // The card locks the programme, so there is no membership mismatch left
      // to report: an empty list is the honest answer rather than a guess.
      programs: [],
    };
  }, [data, request]);
};
